import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from 'lucide-react';
import { fallbackContent } from '../hooks/useContent';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = ({ info = fallbackContent.contact }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing details', description: 'Please enter your name and email.' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.email_sent) {
        toast({ title: 'Message sent!', description: 'Thanks for reaching out. Our team will respond fast.' });
      } else {
        toast({ title: 'Enquiry received!', description: "We've saved your enquiry and will get back to you shortly." });
      }
      setForm({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-[15px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#12263f] focus:ring-1 focus:ring-[#12263f] transition-all';

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#f5f5f8]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <p className="section-label text-[#c9a227] mb-3">Get in Touch</p>
          <h2 className="font-display font-bold text-[#12263f] text-4xl lg:text-5xl">Let's work together.</h2>
          <p className="mt-4 text-gray-600">Reach out for consultancy, products or partnership. We respond fast.</p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${info.phone}`} className="flex items-center gap-3.5 text-gray-700 hover:text-[#12263f] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center"><Phone size={17} className="text-[#12263f]" /></span>
              {info.phone}
            </a>
            <a href={`mailto:${info.email}`} className="flex items-center gap-3.5 text-gray-700 hover:text-[#12263f] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center"><Mail size={17} className="text-[#12263f]" /></span>
              {info.email}
            </a>
            <div className="flex items-center gap-3.5 text-gray-700">
              <span className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center"><MapPin size={17} className="text-[#12263f]" /></span>
              {info.location}
            </div>
          </div>

          <a href={info.whatsapp} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-[#25a866] text-white font-semibold hover:bg-[#1f9257] transition-all shadow-lg shadow-[#25a866]/20">
            <MessageCircle size={18} /> Chat on WhatsApp
          </a>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 lg:p-9 border border-gray-100 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <input name="name" value={form.name} onChange={handleChange} placeholder="Your name" className={inputCls} />
            <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className={inputCls} />
          </div>
          <input name="email" value={form.email} onChange={handleChange} placeholder="Email" className={`${inputCls} mt-4`} />
          <input name="subject" value={form.subject} onChange={handleChange} placeholder="Subject" className={`${inputCls} mt-4`} />
          <textarea name="message" value={form.message} onChange={handleChange} placeholder="How can we help?" rows={5} className={`${inputCls} mt-4 resize-none`} />
          <button type="submit" disabled={loading} className="mt-4 w-full py-3.5 rounded-lg bg-[#12263f] text-white font-semibold hover:bg-[#1c3a5e] transition-all disabled:opacity-70 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
